import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Center,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  StackDivider,
  VStack,
} from '@chakra-ui/react';
import * as React from 'react';

import { Pagination } from '@/components/Pagination';
import { SearchBox } from '@/components/SearchBox';
import { Loading } from '@/imports/ui/chakra/components/Loading';

import {
  GroupAddMembersModal_GroupUsersSearch,
  GroupAddMembersModal_GroupUsersSearchVariables,
} from './graphql-types/GroupAddMembersModal_GroupUsersSearch';

import { Member } from './components/Member';

const GROUP_USER_SEARCH_QUERY = gql`
  query GroupAddMembersModal_GroupUsersSearch($input: GroupUserSearchInput!) {
    groupUserSearch(input: $input) {
      results {
        ...GroupAddMembersMember_UserFragment
      }
      totalCount
    }
  }
  ${Member.userFragment}
`;

type IGroupAddMembersModalProps = {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
};

export const GroupAddMembersModal: React.FC<IGroupAddMembersModalProps> = ({
  isOpen,
  onClose,
  groupId,
}) => {
  const [pageOffset, setPageOffset] = React.useState(0);
  const limit = 50;

  const { data, refetch, loading, error } = useQuery<
    GroupAddMembersModal_GroupUsersSearch,
    GroupAddMembersModal_GroupUsersSearchVariables
  >(GROUP_USER_SEARCH_QUERY, {
    variables: {
      input: {
        offset: pageOffset * 50,
        limit,
      },
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      isCentered
      size="2xl"
      closeOnOverlayClick
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb="0">
          <Heading size="md" mb="4">
            Add Members
          </Heading>
          <SearchBox
            placeholder="Search people"
            onChange={(val) => {
              refetch({
                input: { filter: { searchText: val }, offset: 0, limit: 50 },
              });
            }}
          />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody display="flex" flexDirection="column" py="6">
          <Box overflow="auto">
            {data ? (
              <VStack spacing="4" divider={<StackDivider />}>
                {data.groupUserSearch.results.map((user, i) => {
                  return (
                    <Member key={user._id} member={user} groupId={groupId} />
                  );
                })}
                <Pagination
                  currentPage={pageOffset + 1}
                  numberOfPages={Math.ceil(
                    data.groupUserSearch.totalCount / limit
                  )}
                  onChange={(value) => {
                    setPageOffset(value - 1);
                  }}
                />
              </VStack>
            ) : (
              <Center w="full">
                <Loading />
              </Center>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
